import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import InputAdornment from "@material-ui/core/InputAdornment";
import Icon from "@material-ui/core/Icon";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import CustomDatePicker from "components/DatePicker/CustomDatePicker.jsx";
import DataTable, { formatUGX } from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  heroWrap: {
    position: "relative",
    width: "100%",
    height: "220px",
    borderRadius: "20px",
    overflow: "hidden",
    marginBottom: "30px",
    background: "linear-gradient(160deg, #1a237e 0%, #283593 60%, #3949ab 100%)",
    boxShadow: "0 20px 40px -15px rgba(26, 35, 126, 0.3)"
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%)",
    zIndex: 1
  },
  heroContent: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    padding: "40px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    color: "#fff"
  },
  heroTitle: { fontSize: "32px", fontWeight: 800, margin: 0, letterSpacing: "-0.5px" },
  heroSub: { fontSize: "15px", margin: "8px 0 0", opacity: 0.9, fontWeight: 400 },

  // KPI stat cards
  kpiRow: { display: "flex", gap: "16px", marginBottom: "32px", flexWrap: "wrap" },
  statCard: {
    flex: 1,
    minWidth: "180px",
    padding: "20px",
    borderRadius: "16px",
    background: "#fff",
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    transition: "transform 0.2s ease",
    border: "1px solid #f1f5f9",
    "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 30px 0 rgba(0,0,0,0.08)" }
  },
  iconBox: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  statNumber: { fontSize: "20px", fontWeight: 800, color: "#1e293b", margin: 0 },
  statLabel: { fontSize: "12px", color: "#64748b", margin: "2px 0 0", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.4px" },

  // Toolbar & Search
  toolbarWrap: {
    background: "#fff",
    padding: "20px",
    borderRadius: "16px",
    marginBottom: "24px",
    boxShadow: "0 2px 12px 0 rgba(0,0,0,0.03)",
    border: "1px solid #f1f5f9"
  },
  searchBar: {
    display: "flex",
    gap: "12px",
    marginTop: "16px",
    alignItems: "center",
    flexWrap: "wrap"
  },
  searchInput: {
    flex: 1,
    minWidth: "280px",
    "& .MuiInputBase-root": {
      borderRadius: "10px",
      background: "#f8fafc",
      padding: "4px 12px",
      border: "1px solid #e2e8f0",
      transition: "all 0.2s ease",
      "&:hover": { borderColor: "#cbd5e1" },
      "&.Mui-focused": { background: "#fff", borderColor: "#3f51b5", boxShadow: "0 0 0 4px rgba(63, 81, 181, 0.1)" }
    },
    "& .MuiInput-underline:before, & .MuiInput-underline:after": { display: "none" }
  },
  filterSelect: {
    height: "45px",
    minWidth: "160px",
    borderRadius: "10px",
    padding: "0 12px",
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    fontSize: "14px",
    color: "#475569",
    outline: "none",
    cursor: "pointer",
    "&:focus": { borderColor: "#3f51b5", background: "#fff" }
  },

  tableCard: {
    borderRadius: "16px",
    boxShadow: "0 4px 24px 0 rgba(0,0,0,0.04)",
    border: "1px solid #f1f5f9"
  },
  tableHeader: {
    padding: "20px 24px",
    borderBottom: "1px solid #f1f5f9",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  tableTitle: { fontSize: "18px", fontWeight: 700, color: "#1e293b", margin: 0 },
  tableSubtitle: { fontSize: "13px", color: "#64748b", margin: "2px 0 0" },

  errorMsg: { color: "#f44336", fontSize: "13px" },
  formControl: { width: "100%", marginTop: "16px" },
  actionBtn: { borderRadius: "8px", textTransform: "none", fontWeight: 600 },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 24px",
    borderTop: "1px solid #f1f5f9"
  },
  pageInfo: { fontSize: "13px", color: "#64748b", fontWeight: 500 }
};

const CATEGORIES = ["Tuition", "Salaries", "Infrastructure", "Administration", "Library", "Examination", "ICT", "Catering", "Maintenance", "Other"];
const CURRENT_YEAR = new Date().getFullYear();

const BLANK = { description: "", amount: "", category: "", budgetLineId: "", receiptNo: "", notes: "", date: "" };

class ExpenditureList extends Component {
  state = {
    expenditures: [],
    budgetLines: [],
    loading: true,
    dateFrom: "",
    dateTo: "",
    categoryFilter: "",
    open: false,
    form: { ...BLANK },
    saving: false,
    error: null,
    pagination: { pageIndex: 0, pageSize: 20 },
    total: 0
  };

  componentDidMount() {
    this.fetchBudgetLines();
    this.fetchExpenditures();
  }

  fetchBudgetLines = async () => {
    try {
      const res = await api.get("/finance/budget", { params: { limit: 200, year: CURRENT_YEAR } });
      const d = res.data.data || res.data;
      this.setState({ budgetLines: Array.isArray(d) ? d : d.data || [] });
    } catch (e) { /* silent */ }
  };

  fetchExpenditures = async () => {
    this.setState({ loading: true });
    const { pagination, dateFrom, dateTo, categoryFilter } = this.state;
    try {
      const params = { page: pagination.pageIndex + 1, limit: pagination.pageSize };
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (categoryFilter) params.category = categoryFilter;
      const res = await api.get("/finance/expenditure", { params });
      const d = res.data.data || res.data;
      this.setState({
        expenditures: Array.isArray(d) ? d : d.data || [],
        total: d.total || (Array.isArray(d) ? d.length : d.data?.length || 0),
        loading: false
      });
    } catch (e) {
      this.setState({ loading: false });
    }
  };

  handleChange = field => e => {
    const v = e.target.value;
    this.setState(prev => ({ form: { ...prev.form, [field]: v } }));
  };

  handleFilter = field => e => {
    const value = e?.target?.value ?? "";
    this.setState(prev => ({ [field]: value, pagination: { ...prev.pagination, pageIndex: 0 } }), this.fetchExpenditures);
  };

  handleSearch = e => this.setState({ search: e.target.value });
  handleSearchSubmit = e => { e.preventDefault(); this.setState({ page: 1 }, this.fetchExpenditures); };

  exportCSV = () => {
    const { expenditures, total } = this.state;
    if (!expenditures.length) return;

    const headers = ["Description", "Category", "Amount", "Budget Line", "Date", "Recorded By", "Receipt No"];
    const rows = expenditures.map(e => {
      const line = this.state.budgetLines.find(b => b.id === e.budgetLineId);
      const lineName = line ? `${line.category}${line.description ? " — " + line.description : ""}` : e.budgetLineName || "—";
      return [
        e.description || "",
        e.category || "",
        e.amount || 0,
        lineName,
        e.date ? new Date(e.date).toLocaleDateString("en-UG") : "",
        e.recordedBy || e.createdBy || e.staff?.name || "",
        e.receiptNo || e.reference || ""
      ];
    });

    const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `expenditure_report_${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  handlePaginationChange = updater => {
    const next = typeof updater === "function" ? updater(this.state.pagination) : updater;
    this.setState({ pagination: next }, this.fetchExpenditures);
  };

  openAdd = () => this.setState({ open: true, form: { ...BLANK }, error: null });

  handleSave = async () => {
    const { form } = this.state;
    if (!form.description || !form.amount || !form.category) {
      this.setState({ error: "Description, amount, and category are required." });
      return;
    }
    this.setState({ saving: true, error: null });
    try {
      await api.post("/finance/expenditure", {
        ...form,
        amount: parseFloat(form.amount),
        date: form.date || new Date().toISOString().substring(0, 10)
      });
      this.setState({ open: false, saving: false });
      this.fetchExpenditures();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed to save expenditure.", saving: false });
    }
  };

  render() {
    const { classes } = this.props;
    const { expenditures, budgetLines, loading, dateFrom, dateTo, categoryFilter, open, form, saving, error, pagination, total } = this.state;

    const totalAmount = expenditures.reduce((s, e) => s + (e.amount || 0), 0);

    const columns = [
      {
        id: "description",
        header: "Description",
        accessorFn: e => e.description || "—",
        cell: info => <div style={{ fontWeight: 600, color: "#1e293b" }}>{info.getValue()}</div>
      },
      {
        id: "category",
        header: "Category",
        accessorFn: e => e.category || "—",
        cell: info => (
          <Chip
            label={info.getValue()}
            size="small"
            style={{ background: "#f1f5f9", color: "#475569", fontWeight: 500, fontSize: "11px", borderRadius: "6px" }}
          />
        )
      },
      {
        id: "amount",
        header: "Amount (UGX)",
        accessorFn: e => e.amount || 0,
        meta: { align: "right" },
        cell: info => <strong style={{ color: "#1a237e" }}>{formatUGX(info.getValue())}</strong>
      },
      {
        id: "budgetLine",
        header: "Budget Line",
        accessorFn: e => {
          const line = budgetLines.find(b => b.id === e.budgetLineId);
          return line ? `${line.category}${line.description ? " — " + line.description : ""}` : e.budgetLineName || "—";
        },
        cell: info => <span style={{ fontSize: "13px", color: "#64748b" }}>{info.getValue()}</span>
      },
      {
        id: "date",
        header: "Date",
        accessorFn: e => (e.date ? new Date(e.date).toLocaleDateString("en-UG") : "—")
      },
      {
        id: "recordedBy",
        header: "Recorded By",
        accessorFn: e => e.recordedBy || e.createdBy || e.staff?.name || "—",
        cell: info => <span style={{ fontSize: "13px", color: "#64748b" }}>{info.getValue()}</span>
      },
      {
        id: "receiptNo",
        header: "Receipt No",
        accessorFn: e => e.receiptNo || e.reference || e.paymentRef || "—",
        meta: { variant: "mono" }
      }
    ];

    const statCards = [
      { label: "Total Transactions", value: total, icon: "receipt_long", bg: "#e8eaf6", text: "#1a237e" },
      { label: "Total Amount (Page)", value: `UGX ${totalAmount.toLocaleString()}`, icon: "payments", bg: "#e8f5e9", text: "#2e7d32" },
      { label: "Date Range", value: dateFrom && dateTo ? `${dateFrom} to ${dateTo}` : "All Time", icon: "date_range", bg: "#fff8e1", text: "#e65100" }
    ];

    return (
      <div>
        {/* ── Hero Section ─────────────────────────────────────────────── */}
        <div className={classes.heroWrap}>
          <div className={classes.heroOverlay} />
          <div className={classes.heroContent}>
            <h1 className={classes.heroTitle}>Expenditure Tracker</h1>
            <p className={classes.heroSub}>Track and manage institutional spending, vouchers, and budget alignment</p>
            <div style={{ marginTop: "24px", display: "flex", gap: "12px" }}>
              <Button color="white" className={classes.actionBtn} onClick={this.openAdd}>
                <Icon style={{ marginRight: "8px" }}>add_circle</Icon> Record Expenditure
              </Button>
              <Button color="white" simple className={classes.actionBtn} onClick={this.exportCSV} disabled={!expenditures.length}>
                <Icon style={{ marginRight: "8px" }}>file_download</Icon> Export CSV
              </Button>
            </div>
          </div>
        </div>

        {/* ── KPI Stats ────────────────────────────────────────────────── */}
        <div className={classes.kpiRow}>
          {statCards.map(s => (
            <div key={s.label} className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: s.bg }}>
                <Icon style={{ color: s.text }}>{s.icon}</Icon>
              </div>
              <div>
                <h3 className={classes.statNumber}>{s.value}</h3>
                <p className={classes.statLabel}>{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Filters & Search ─────────────────────────────────────────── */}
        <div className={classes.toolbarWrap}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h4 style={{ margin: 0, fontWeight: 700, color: "#1e293b" }}>Filters</h4>
            <Button 
              simple 
              size="sm" 
              color="primary" 
              style={{ textTransform: "none" }}
              onClick={() => this.setState(prev => ({ dateFrom: "", dateTo: "", categoryFilter: "", pagination: { ...prev.pagination, pageIndex: 0 } }), this.fetchExpenditures)}
            >
              Clear All Filters
            </Button>
          </div>
          <div className={classes.searchBar}>
             <CustomDatePicker
                labelText="From Date"
                id="expDateFrom"
                formControlProps={{ className: classes.searchInput, style: { margin: 0, paddingTop: 0 } }}
                inputProps={{ value: dateFrom, onChange: this.handleFilter("dateFrom") }}
              />
              <CustomDatePicker
                labelText="To Date"
                id="expDateTo"
                formControlProps={{ className: classes.searchInput, style: { margin: 0, paddingTop: 0 } }}
                inputProps={{ value: dateTo, onChange: this.handleFilter("dateTo") }}
              />
              <FormControl className={classes.formControl} style={{ minWidth: "200px", marginTop: 0 }}>
                <Select
                  value={categoryFilter}
                  onChange={this.handleFilter("categoryFilter")}
                  className={classes.filterSelect}
                  displayEmpty
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
          </div>
        </div>

        <GridContainer>
          <GridItem xs={12}>
            <DataTable
              title="Expenditure Records"
              subtitle={`${total} total record${total !== 1 ? "s" : ""}`}
              columns={columns}
              data={expenditures}
              isLoading={loading}
              emptyState="No expenditure records found matching your criteria."
              pagination={pagination}
              onPaginationChange={this.handlePaginationChange}
              totalCount={total}
            />
          </GridItem>
        </GridContainer>

        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title="Record Expenditure" />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <CustomInput labelText="Description *" id="expDesc" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.description, onChange: this.handleChange("description") }} />
            <FormControl className={classes.formControl}>
              <InputLabel>Category *</InputLabel>
              <Select value={form.category} onChange={this.handleChange("category")}>
                <MenuItem value="">— Select —</MenuItem>
                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <CustomInput labelText="Amount (UGX) *" id="expAmount" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.amount, onChange: this.handleChange("amount"), type: "number", min: "0" }} />
            <FormControl className={classes.formControl}>
              <InputLabel>Budget Line</InputLabel>
              <Select value={form.budgetLineId} onChange={this.handleChange("budgetLineId")}>
                <MenuItem value="">— None —</MenuItem>
                {budgetLines.map(b => (
                  <MenuItem key={b.id} value={b.id}>{b.category}{b.description ? ` — ${b.description}` : ""}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <CustomDatePicker labelText="Date" id="expDate" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.date, onChange: this.handleChange("date") }} />
            <CustomInput labelText="Receipt / Voucher No" id="expReceipt" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.receiptNo, onChange: this.handleChange("receiptNo") }} />
            <div style={{ marginTop: "16px" }}>
              <label style={{ fontSize: "12px", color: "#aaa", textTransform: "uppercase" }}>Notes</label>
              <textarea
                rows={3}
                value={form.notes}
                onChange={this.handleChange("notes")}
                style={{ width: "100%", marginTop: "4px", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px", resize: "vertical", boxSizing: "border-box" }}
              />
            </div>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Record"}
            </Button>
          </DialogFooter>
        </Dialog>
      </div>
    );
  }
}

ExpenditureList.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(ExpenditureList));
